import * as LabelPrimitive from '@radix-ui/react-label'
import { TriangleAlert } from 'lucide-react'
import { useId } from 'react'
import { cn } from '../cn'

export interface FieldProps {
  label: string
  hint?: string
  /** A soft correction the field applied on its own — e.g. a value clamped
   * to the range the server accepts. Distinct from `error`: the field still
   * submits fine, but what is used no longer matches what is shown, so this
   * needs real visual weight (amber, not the muted hint grey) and an
   * accessible signal (announced via `role="status"` as it appears),
   * without touching the text the user typed. */
  warning?: string
  error?: string
  required?: boolean
  className?: string
  children: (props: { id: string; invalid: boolean; warning: boolean; describedBy?: string }) => React.ReactNode
}

/**
 * Label, control, and message as one unit, so the wiring between them
 * cannot drift: the message is always announced with the input.
 */
export function Field({ label, hint, warning, error, required, className, children }: FieldProps) {
  const id = useId()
  const messageId = `${id}-message`
  const message = error ?? warning ?? hint
  const tone = error ? 'error' : warning ? 'warning' : 'hint'
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {/* The required marker sits outside the label element on purpose. The
          control already carries `required`, so the asterisk is decoration —
          and keeping it out means the label's text is exactly the field name,
          which is what assistive technology announces and what test tooling
          matches on. */}
      <span className="flex items-center gap-0.5">
        <LabelPrimitive.Root htmlFor={id} className="text-xs font-medium text-ink-muted">
          {label}
        </LabelPrimitive.Root>
        {required ? <span aria-hidden className="text-xs text-danger">*</span> : null}
      </span>
      {children({
        id,
        invalid: Boolean(error),
        warning: Boolean(warning),
        describedBy: message ? messageId : undefined,
      })}
      {message ? (
        <p
          id={messageId}
          // A live region only for the warning tone: it is the one message
          // that can appear *while the user keeps typing*, with no focus
          // change to carry it to assistive tech otherwise. A static error
          // or hint is already reachable via aria-describedby.
          role={tone === 'warning' ? 'status' : undefined}
          className={cn(
            'flex items-center gap-1 text-xs',
            tone === 'error' && 'text-danger',
            tone === 'warning' && 'font-medium text-warning',
            tone === 'hint' && 'text-ink-faint',
          )}
        >
          {tone === 'warning' ? <TriangleAlert aria-hidden className="size-3 shrink-0" /> : null}
          {message}
        </p>
      ) : null}
    </div>
  )
}
